import { Printer } from '@models/payload/printer';
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import {
  createPrinter as createPrinterApi,
  deletePrinter,
  editPrinter,
  getPrinterDetail,
  getPrinters,
} from '@services/printers';
import { createAppAsyncThunk } from '../utils/reduxUtil';

interface PrintersStateInterface {
  loading: boolean;
  printers: Printer[];
  detail: Printer | undefined;
}

const initialState: PrintersStateInterface = {
  loading: false,
  printers: [],
  detail: undefined,
};

interface GetPrinterInput {
  vendorId?: string;
  id: string;
}

interface GetVendorPrinterByIdResponse {
  data: {
    printer: Printer | undefined;
  };
}

const getPrinterById = createAppAsyncThunk<GetVendorPrinterByIdResponse, GetPrinterInput>(
  'printers/getVendorPrinterById',
  async ({ id }) => {
    const res = await getPrinterDetail(id);
    return { data: { printer: res.data } };
  }
);

interface GetVendorPrintersResponse {
  data: {
    printers: Printer[];
  };
}

const getVendorPrinters = createAppAsyncThunk<GetVendorPrintersResponse>('printers/getVendorPrinters', async () => {
  const res = await getPrinters();
  return { data: { printers: res.data || [] } };
});

interface CreatePrinterInput {
  vendorId: string;
  name: string;
  ip?: string;
}

const createPrinter = createAppAsyncThunk<void, CreatePrinterInput>(
  'printers/createVendorPrinters',
  async ({ vendorId, name, ip }, { dispatch }) => {
    await createPrinterApi({ ip, name, vendorId });
    await dispatch(getVendorPrinters());
  }
);

interface UpdatePrinterInput {
  vendorId?: string;
  id: string;
  name: string;
  ip?: string;
}

const updatePrinterById = createAppAsyncThunk<GetVendorPrinterByIdResponse, UpdatePrinterInput>(
  'printers/updateVendorPrinters',
  async ({ id, name, ip }, { dispatch }) => {
    const res = await editPrinter({ id, ip, name });
    await dispatch(getVendorPrinters());

    return { data: { printer: res.data } };
  }
);

interface DeletePrinterInput {
  vendorId?: string;
  id: string;
}

const deletePrinterById = createAppAsyncThunk<void, DeletePrinterInput>(
  'printers/deleteVendorPrinters',
  async ({ id }, { dispatch }) => {
    await deletePrinter(id);
    await dispatch(getVendorPrinters());
  }
);

const printersSlice = createSlice({
  name: 'printers',
  initialState,
  reducers: {
    clearDetail: (state) => ({
      ...state,
      detail: undefined,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<PrintersStateInterface>) => {
    builder.addCase(getVendorPrinters.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getVendorPrinters.fulfilled, (state, action) => {
      const { printers } = action.payload.data;

      return {
        ...state,
        loading: false,
        printers,
      };
    });

    builder.addCase(createPrinter.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getPrinterById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getPrinterById.fulfilled, (state, action) => {
      const { printer } = action.payload.data;

      return {
        ...state,
        loading: false,
        detail: printer,
      };
    });

    builder.addCase(updatePrinterById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(updatePrinterById.fulfilled, (state, action) => {
      const { printer } = action.payload.data;

      return {
        ...state,
        loading: false,
        detail: printer,
      };
    });

    builder.addCase(deletePrinterById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(deletePrinterById.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(deletePrinterById.rejected, (state) => ({
      ...state,
      loading: false,
    }));
  },
});

export default printersSlice;
export { createPrinter, deletePrinterById, getPrinterById, getVendorPrinters, updatePrinterById };
